import React, { useState, useMemo, useCallback, useRef } from "react";
import Autocomplete, { AutocompleteProps } from "@mui/material/Autocomplete";
import Button, { ButtonProps } from "@mui/material/Button";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import Typography, { TypographyProps } from "@mui/material/Typography";
import Box, { BoxProps } from "@mui/material/Box";
import Stack, { StackProps } from "@mui/material/Stack";
import Popover, { PopoverProps } from "@mui/material/Popover";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { MessageResponse } from "../../types/common/commonTypes";
import {
  PRIMARY_ORDER_STATUSES,
  PrimaryOrderStatusType,
  APPROVAL_STATUSES,
  ApprovalStatusType,
  SALES_ORD_FILL_STATUSES,
  SalesOrderFulfillmentStatusType,
} from "../../types/common/statusTypes";
import { OrderStatusUpdate } from "../../types/combinedTypes";
import {
  PURCH_ORD_REC_STATUSES,
  PurchaseOrderReceiptStatusType,
} from "../../types/purchOrderTypes";
import {
  WORK_ORDER_BUILD_STATUSES,
  WorkOrderBuildStatusType,
  WORK_ORD_PART_STATUSES,
  WorkOrderPartStatusType,
} from "../../types/workOrderTypes";
import {
  TypedSelectOption,
  SelectOptionRegistry,
} from "../../types/common/selectOptions";
import { UseMutationResult } from "@tanstack/react-query";
import stringUtils from "../../utils/stringUtils";

type OrdStatOption = SelectOptionRegistry["primary_order_status"];
const orderStatusOpts: OrdStatOption[] = (() => {
  return PRIMARY_ORDER_STATUSES.map((val) => ({
    label: stringUtils.capFirstLetter(val.replace(/[_]/g, " ")),
    type: "order_status",
    value: { id: val },
  }));
})();
type AppStatOption = TypedSelectOption<"approval_status">;
const approvalStatusOpts: AppStatOption[] = (() => {
  return APPROVAL_STATUSES.map((val) => ({
    label: stringUtils.capFirstLetter(val.replace(/[_]/g, " ")),
    type: "approval_status",
    value: { id: val },
  }));
})();
type POStatOption = TypedSelectOption<"po_status">;
const poStatusOpts: POStatOption[] = (() => {
  return PURCH_ORD_REC_STATUSES.map((val) => ({
    label: stringUtils.capFirstLetter(val.replace(/[_]/g, " ")),
    type: "po_status",
    value: { id: val },
  }));
})();
type SOStatOption = TypedSelectOption<"so_status">;
const soStatusOpts: SOStatOption[] = (() => {
  return SALES_ORD_FILL_STATUSES.map((val) => ({
    label: stringUtils.capFirstLetter(val.replace(/[_]/g, " ")),
    type: "so_status",
    value: { id: val },
  }));
})();
type WOStatOption = TypedSelectOption<"wo_status">;
const woStatusOpts: WOStatOption[] = (() => {
  return WORK_ORDER_BUILD_STATUSES.map((val) => ({
    label: stringUtils.capFirstLetter(val.replace(/[_]/g, " ")),
    type: "wo_status",
    value: { id: val },
  }));
})();
type WOPartOption = TypedSelectOption<"wo_part_status">;
const woPartOpts: WOPartOption[] = (() => {
  return WORK_ORD_PART_STATUSES.map((val) => ({
    label: stringUtils.capFirstLetter(val.replace(/[_]/g, " ")),
    type: "wo_part_status",
    value: { id: val },
  }));
})();

interface OrderStatusActionsProps {
  recordType: "work_order" | "sales_order" | "purchase_order";
  recordId: string;
  disabled?: boolean;
  orderStatus: PrimaryOrderStatusType;
  approvalStatus: ApprovalStatusType;
  poStatus?: PurchaseOrderReceiptStatusType;
  soStatus?: SalesOrderFulfillmentStatusType;
  woStatus?: WorkOrderBuildStatusType;
  woPartStatus?: WorkOrderPartStatusType;
  mutation: UseMutationResult<
    MessageResponse,
    Error,
    {
      data: OrderStatusUpdate;
    },
    unknown
  >;
  onStatusUpdateComplete: (response: MessageResponse) => void;
}

const OrderStatusActions: React.FC<OrderStatusActionsProps> = ({
  recordType,
  recordId,
  disabled,
  orderStatus,
  approvalStatus,
  poStatus,
  soStatus,
  woStatus,
  woPartStatus,
  mutation,
  onStatusUpdateComplete,
}) => {
  const [anchorEl, setAnchorEl] = useState<any>(null);
  const panelOpen = Boolean(anchorEl);
  const startingOrdOption: OrdStatOption | null = useMemo(() => {
    console.log("Order Status", orderStatus);
    return orderStatusOpts.find((opt) => opt.value.id === orderStatus) || null;
  }, [orderStatus]);
  const startingAppOption: AppStatOption | null = useMemo(() => {
    return (
      approvalStatusOpts.find((opt) => opt.value.id === approvalStatus) || null
    );
  }, [approvalStatus]);
  const startingPOOption: POStatOption | null = useMemo(() => {
    return poStatusOpts.find((opt) => opt.value.id === poStatus) || null;
  }, [poStatus]);
  const startingSOOption: SOStatOption | null = useMemo(() => {
    return soStatusOpts.find((opt) => opt.value.id === soStatus) || null;
  }, [soStatus]);
  const startingWOOption: WOStatOption | null = useMemo(() => {
    return woStatusOpts.find((opt) => opt.value.id === woStatus) || null;
  }, [woStatus]);
  const startingWOPartOption: WOPartOption | null = useMemo(() => {
    return woPartOpts.find((opt) => opt.value.id === woPartStatus) || null;
  }, [woPartStatus]);
  const [updateObj, setUpdateObj] = useState<{
    order_status: OrdStatOption | null;
    approval_status: AppStatOption | null;
    po_status: POStatOption | null;
    so_status: SOStatOption | null;
    wo_status: WOStatOption | null;
    wo_part_status: WOPartOption | null;
    mark_void: boolean;
    mark_complete: boolean;
  }>({
    order_status: startingOrdOption,
    approval_status: startingAppOption,
    po_status: startingPOOption,
    so_status: startingSOOption,
    wo_status: startingWOOption,
    wo_part_status: startingWOPartOption,
    mark_void: false,
    mark_complete: false,
  });

  const handleSubmit = async () => {
    const payload: OrderStatusUpdate = {
      record_type: recordType,
      record_id: recordId,
      order_status:
        (updateObj.order_status?.value.id as PrimaryOrderStatusType) ||
        undefined,
      approval_status:
        (updateObj.approval_status?.value.id as ApprovalStatusType) ||
        undefined,
      po_status:
        (updateObj.po_status?.value.id as PurchaseOrderReceiptStatusType) ||
        undefined,
      so_status:
        (updateObj.so_status?.value.id as SalesOrderFulfillmentStatusType) ||
        undefined,
      wo_status:
        (updateObj.wo_status?.value.id as WorkOrderBuildStatusType) ||
        undefined,
      wo_part_status:
        (updateObj.wo_part_status?.value.id as WorkOrderPartStatusType) ||
        undefined,
      mark_complete: updateObj.mark_complete || undefined,
      mark_void: updateObj.mark_void || undefined,
    };
    const results = await mutation.mutateAsync({ data: payload });
    onStatusUpdateComplete(results);
    setAnchorEl(null);
  };

  return (
    <Box display="flex">
      <Button
        onClick={(event) => setAnchorEl(event.currentTarget)}
        disabled={disabled}
        variant="outlined"
        color="info"
      >
        Status Actions
      </Button>
      <Popover
        open={panelOpen}
        anchorEl={anchorEl}
        onClose={(e, r) => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              height: 300,
              width: 600,
              overflow: "auto",
            },
          },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gridAutoRows: "auto",
            columnGap: 1,
            rowGap: 1,
            p: 2,
            width: "100%",
          }}
        >
          <FormControlLabel
            sx={{
              gridColumn: 1,
              gridRow: 1,
              width: "100%",
            }}
            control={
              <Switch
                size="small"
                value={updateObj.mark_complete}
                disabled={orderStatus === "COMPLETE" || updateObj.mark_void}
                onChange={(event, checked) => {
                  setUpdateObj((prev) => ({
                    ...prev,
                    mark_complete: checked,
                  }));
                }}
              />
            }
            label="Mark Order Complete?"
            labelPlacement="bottom"
          />
          <FormControlLabel
            sx={{
              gridColumn: 2,
              gridRow: 1,
              width: "100%",
            }}
            control={
              <Switch
                size="small"
                value={updateObj.mark_void}
                disabled={orderStatus === "VOID" || updateObj.mark_complete}
                onChange={(event, checked) => {
                  setUpdateObj((prev) => ({
                    ...prev,
                    mark_void: checked,
                  }));
                }}
              />
            }
            label="Mark Order Void?"
            labelPlacement="bottom"
          />
          <Autocomplete
            fullWidth
            sx={{
              gridColumn: 1,
              gridRow: 2,
            }}
            value={updateObj.order_status}
            onChange={(e, v) =>
              setUpdateObj((prev) => ({
                ...prev,
                order_status: v,
              }))
            }
            disabled={updateObj.mark_complete || updateObj.mark_void}
            options={orderStatusOpts}
            getOptionLabel={(option) => option?.label}
            renderInput={(params) => (
              <TextField {...params} label="Order Status" />
            )}
          />
          {recordType !== "sales_order" && (
            <Autocomplete
              fullWidth
              sx={{
                gridColumn: 2,
                gridRow: 2,
              }}
              value={updateObj.approval_status}
              onChange={(e, v) =>
                setUpdateObj((prev) => ({
                  ...prev,
                  approval_status: v,
                }))
              }
              disabled={updateObj.mark_complete || updateObj.mark_void}
              options={approvalStatusOpts}
              getOptionLabel={(option) => option?.label}
              renderInput={(params) => (
                <TextField {...params} label="Approval Status" />
              )}
            />
          )}
          {recordType === "purchase_order" ? (
            <Autocomplete
              fullWidth
              sx={{
                gridColumn: 1,
                gridRow: 3,
              }}
              value={updateObj.po_status}
              onChange={(e, v) =>
                setUpdateObj((prev) => ({
                  ...prev,
                  po_status: v,
                }))
              }
              disabled={updateObj.mark_complete || updateObj.mark_void}
              options={poStatusOpts}
              getOptionLabel={(option) => option?.label}
              renderInput={(params) => (
                <TextField {...params} label="Receipt Status" />
              )}
            />
          ) : recordType === "sales_order" ? (
            <Autocomplete
              fullWidth
              sx={{
                gridColumn: 1,
                gridRow: 3,
              }}
              value={updateObj.so_status}
              onChange={(e, v) =>
                setUpdateObj((prev) => ({
                  ...prev,
                  so_status: v,
                }))
              }
              disabled={updateObj.mark_complete || updateObj.mark_void}
              options={soStatusOpts}
              getOptionLabel={(option) => option?.label}
              renderInput={(params) => (
                <TextField {...params} label="Fulfillment Status" />
              )}
            />
          ) : (
            <>
              <Autocomplete
                fullWidth
                sx={{
                  gridColumn: 1,
                  gridRow: 3,
                }}
                value={updateObj.wo_status}
                onChange={(e, v) =>
                  setUpdateObj((prev) => ({
                    ...prev,
                    wo_status: v,
                  }))
                }
                disabled={updateObj.mark_complete || updateObj.mark_void}
                options={woStatusOpts}
                getOptionLabel={(option) => option?.label}
                renderInput={(params) => (
                  <TextField {...params} label="Build Status" />
                )}
              />
              <Autocomplete
                fullWidth
                sx={{
                  gridColumn: 2,
                  gridRow: 3,
                }}
                value={updateObj.wo_part_status}
                onChange={(e, v) =>
                  setUpdateObj((prev) => ({
                    ...prev,
                    wo_part_status: v,
                  }))
                }
                disabled={updateObj.mark_complete || updateObj.mark_void}
                options={woPartOpts}
                getOptionLabel={(option) => option?.label}
                renderInput={(params) => (
                  <TextField {...params} label="Part Status" />
                )}
              />
            </>
          )}
          <Button
            sx={{
              gridColumn: 2,
              gridRow: 4,
            }}
            variant="contained"
            color="success"
            onClick={(e) => {
              handleSubmit();
            }}
          >
            Update Status
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

export default OrderStatusActions;
